/**
 * useAgentStream — Core hook that consumes the SSE stream and manages
 * message state, tool indicators, and component data.
 *
 * This is the heart of the UI: it takes raw SSE events and produces
 * typed state that the chat UI and visual components consume.
 */

import { useState, useCallback, useRef } from "react";
import { streamMessage } from "@/api/sse";
import type { Message, ComponentData, UsageData, SSEEvent } from "@/api/types";

/** Callbacks for deal-related SSE events, bridging to DealContext */
export interface DealCallbacks {
  onDealContext?: (event: {
    deal_id: string;
    parcel_id?: string;
    address?: string;
    status: string;
    title?: string;
    completeness_score: number;
  }) => void;
  onDSRUpdate?: (event: {
    deal_id: string;
    completeness_score: number;
    pir_status: Record<string, string>;
    ground_truth: Record<string, unknown>;
    estimates: Record<string, unknown>;
  }) => void;
  onChipsUpdate?: (chips: Array<{ label: string; message: string; category: string }>) => void;
  onTitleUpdate?: (title: string) => void;
}

let messageIdCounter = 0;
function nextId(): string {
  return `msg_${++messageIdCounter}_${Date.now()}`;
}

interface ToolIndicator {
  name: string;
  status: "running" | "done";
}

interface AgentStreamState {
  messages: Message[];
  isStreaming: boolean;
  toolIndicators: ToolIndicator[];
  usage: UsageData | null;
  error: string | null;
}

interface AgentStreamActions {
  /** Send a user message and stream the response. Optional conversationId override for newly created conversations. */
  send: (content: string, conversationIdOverride?: string) => Promise<void>;
  /** Load existing messages into state (from conversation history) */
  loadMessages: (msgs: Message[]) => void;
  /** Clear all messages */
  clear: () => void;
}

export function useAgentStream(
  conversationId: string | null,
  dealCallbacks?: DealCallbacks
): AgentStreamState & AgentStreamActions {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [toolIndicators, setToolIndicators] = useState<ToolIndicator[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track the current assistant message being built
  const currentTextRef = useRef("");
  const currentComponentsRef = useRef<ComponentData[]>([]);
  const currentToolCallsRef = useRef<Array<{ name: string; args: Record<string, unknown> }>>([]);
  const assistantIdRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async (content: string, conversationIdOverride?: string) => {
      const effectiveConvoId = conversationIdOverride || conversationId;
      if (!effectiveConvoId) return;

      setError(null);
      setIsStreaming(true);
      setToolIndicators([]);

      // Reset accumulators
      currentTextRef.current = "";
      currentComponentsRef.current = [];
      currentToolCallsRef.current = [];
      assistantIdRef.current = null;

      // Add user message
      const userMsg: Message = {
        id: nextId(),
        role: "user",
        content,
      };
      setMessages((prev) => [...prev, userMsg]);

      // Create abort controller
      abortRef.current = new AbortController();

      const handleEvent = (event: SSEEvent) => {
        switch (event.type) {
          case "token": {
            // Ensure we have an assistant message
            if (!assistantIdRef.current) {
              assistantIdRef.current = nextId();
            }
            currentTextRef.current += event.text;

            const id = assistantIdRef.current;
            const text = currentTextRef.current;
            const components = [...currentComponentsRef.current];
            const toolCalls = [...currentToolCallsRef.current];

            setMessages((prev) => {
              const existing = prev.findIndex((m) => m.id === id);
              const msg: Message = {
                id,
                role: "assistant",
                content: text,
                components: components.length > 0 ? components : undefined,
                toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
                isStreaming: true,
              };
              if (existing >= 0) {
                const next = [...prev];
                next[existing] = msg;
                return next;
              }
              return [...prev, msg];
            });
            break;
          }

          case "thinking": {
            // Thinking text goes to tool indicators, not message content
            break;
          }

          case "tool_start": {
            currentToolCallsRef.current.push({
              name: event.name,
              args: event.args,
            });

            setToolIndicators((prev) => {
              // Mark all previous as done
              const updated = prev.map((t) => ({
                ...t,
                status: "done" as const,
              }));
              return [...updated, { name: event.name, status: "running" }];
            });
            break;
          }

          case "tool_result": {
            setToolIndicators((prev) =>
              prev.map((t) =>
                t.name === event.name && t.status === "running"
                  ? { ...t, status: "done" }
                  : t
              )
            );
            break;
          }

          case "component": {
            // Visual tool result — replace any previous component of the same tool
            // (e.g., if agent retries find_comparable_sales with wider radius)
            const comp: ComponentData = {
              id: `comp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
              tool: event.tool,
              data: event.data,
            };
            const prevIdx = currentComponentsRef.current.findIndex(
              (c) => c.tool === event.tool
            );
            if (prevIdx >= 0) {
              currentComponentsRef.current[prevIdx] = comp;
            } else {
              currentComponentsRef.current.push(comp);
            }

            // If we already have an assistant message in progress, update it
            if (assistantIdRef.current) {
              const id = assistantIdRef.current;
              const components = [...currentComponentsRef.current];

              setMessages((prev) => {
                const idx = prev.findIndex((m) => m.id === id);
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = { ...next[idx]!, components };
                  return next;
                }
                return prev;
              });
            }
            break;
          }

          case "deal_context": {
            dealCallbacks?.onDealContext?.(event);
            break;
          }

          case "dsr_update": {
            dealCallbacks?.onDSRUpdate?.(event);
            break;
          }

          case "chips_update": {
            dealCallbacks?.onChipsUpdate?.(event.chips);
            break;
          }

          case "title_update": {
            dealCallbacks?.onTitleUpdate?.(event.title);
            break;
          }

          case "queued": {
            // Mark the latest user message as queued
            setMessages((prev) => {
              const last = [...prev];
              for (let i = last.length - 1; i >= 0; i--) {
                if (last[i]!.role === "user") {
                  last[i] = { ...last[i]!, isQueued: true };
                  break;
                }
              }
              return last;
            });
            break;
          }

          case "dequeued": {
            // Clear queued flag
            setMessages((prev) =>
              prev.map((m) => (m.isQueued ? { ...m, isQueued: false } : m))
            );
            break;
          }

          case "usage": {
            setUsage(event.data);
            break;
          }

          case "done": {
            // Finalize the assistant message
            if (assistantIdRef.current) {
              const id = assistantIdRef.current;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === id ? { ...m, isStreaming: false } : m
                )
              );
            }
            setToolIndicators((prev) =>
              prev.map((t) => ({ ...t, status: "done" as const }))
            );
            break;
          }

          case "error": {
            setError(event.error);
            break;
          }
        }
      };

      try {
        await streamMessage(effectiveConvoId, content, {
          onEvent: handleEvent,
          onError: (err) => setError(err.message),
          signal: abortRef.current.signal,
        });
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [conversationId]
  );

  const loadMessages = useCallback((msgs: Message[]) => {
    setMessages(msgs);
  }, []);

  const clear = useCallback(() => {
    setMessages([]);
    setToolIndicators([]);
    setUsage(null);
    setError(null);
    // Cancel any in-flight stream
    abortRef.current?.abort();
  }, []);

  return {
    messages,
    isStreaming,
    toolIndicators,
    usage,
    error,
    send,
    loadMessages,
    clear,
  };
}
